import React from 'react'
import { useNls } from '../../nls/nlsProvider'
import type { EduReportCardSnapshot } from '../../services/eduReportCardService'
import './reportCardDocument.css'

export interface ReportCardDocumentProps {
  snapshot: EduReportCardSnapshot
  kind?: 'REPORT_CARD' | 'PROGRESS_REPORT'
  signatureUrl?: string | null
}

export const ReportCardDocument: React.FC<ReportCardDocumentProps> = ({
  snapshot,
  kind = 'REPORT_CARD',
  signatureUrl,
}) => {
  const { t } = useNls()

  const isProgressReport = kind === 'PROGRESS_REPORT'
  const title = isProgressReport
    ? t('NLSEducationDocumentProgressReportTitle')
    : t('NLSEducationDocumentReportCardTitle')

  return (
    <div className="reportCardDocument" data-testid="report-card-document">
      {/* Header */}
      <div className="reportCardDocument__header">
        <div className="reportCardDocument__schoolInfo">
          <span className="reportCardDocument__schoolName">{snapshot?.school?.name}</span>
          <span className="reportCardDocument__title">{title}</span>
        </div>
        {snapshot?.school?.logoUrl &&
          snapshot.school.logoUrl.startsWith('https://') && (
            <img
              src={snapshot.school.logoUrl}
              alt={t('NLSEducationDocumentSchoolLogoAlt')}
              className="reportCardDocument__logo"
            />
          )}
      </div>

      {/* Details Grid */}
      <div className="reportCardDocument__detailsGrid">
        <div className="reportCardDocument__detailItem">
          <span className="reportCardDocument__detailLabel">{t('NLSEducationDocumentLearnerLabel')}</span>
          <span className="reportCardDocument__detailValue">{snapshot?.learner?.displayName}</span>
        </div>
        {snapshot?.learner?.rollNo && (
          <div className="reportCardDocument__detailItem">
            <span className="reportCardDocument__detailLabel">{t('NLSEducationDocumentRollNoLabel')}</span>
            <span className="reportCardDocument__detailValue">{snapshot.learner.rollNo}</span>
          </div>
        )}
        {snapshot?.learner?.className && (
          <div className="reportCardDocument__detailItem">
            <span className="reportCardDocument__detailLabel">{t('NLSEducationDocumentClassLabel')}</span>
            <span className="reportCardDocument__detailValue">{snapshot.learner.className}</span>
          </div>
        )}
        <div className="reportCardDocument__detailItem">
          <span className="reportCardDocument__detailLabel">{t('NLSEducationDocumentTermLabel')}</span>
          <span className="reportCardDocument__detailValue">{snapshot?.term?.name}</span>
        </div>
      </div>

      {/* Academic Performance Table */}
      {snapshot?.subjects && snapshot.subjects.length > 0 && (
        <div className="reportCardDocument__section">
          <span className="reportCardDocument__sectionTitle">
            {t('NLSEducationDocumentAcademicPerformance')}
          </span>
          <table className="reportCardDocument__table">
            <thead>
              <tr>
                <th>{t('NLSEducationDocumentSubjectCol')}</th>
                <th>{t('NLSEducationDocumentCategoriesCol')}</th>
                <th>{t('NLSEducationDocumentTermPercentCol')}</th>
                <th>{t('NLSEducationDocumentTermGradeCol')}</th>
                <th>{t('NLSEducationDocumentYearPercentCol')}</th>
                <th>{t('NLSEducationDocumentYearGradeCol')}</th>
                <th>{t('NLSEducationDocumentRemarksHeader')}</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.subjects.map((subj, idx) => (
                <tr key={subj.subjectNodeId || idx}>
                  <td><strong>{subj.name}</strong></td>
                  <td>
                    {subj.categoryAverages &&
                      Object.entries(subj.categoryAverages).map(([cat, avg]) => (
                        <span key={cat} className="reportCardDocument__catAvg">
                          {cat}: <span>{avg != null ? `${avg}%` : '—'}</span>
                        </span>
                      ))}
                  </td>
                  <td>{subj.termPercent != null ? `${subj.termPercent}%` : '—'}</td>
                  <td>{subj.termGrade || '—'}</td>
                  <td>{subj.yearPercent != null ? `${subj.yearPercent}%` : '—'}</td>
                  <td>{subj.yearGrade || '—'}</td>
                  <td>{subj.subjectRemark || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Training Progress (PROGRESS_REPORT only) */}
      {isProgressReport && snapshot?.progress && (
        <div className="reportCardDocument__section">
          <span className="reportCardDocument__sectionTitle">
            {t('NLSEducationDocumentProgressSectionTitle')}
          </span>
          {snapshot.progress.metrics && snapshot.progress.metrics.length > 0 && (
            <table className="reportCardDocument__table">
              <thead>
                <tr>
                  <th>{t('NLSEducationDocumentProgressMetricCol')}</th>
                  <th>{t('NLSEducationDocumentProgressInitialCol')}</th>
                  <th>{t('NLSEducationDocumentProgressLatestCol')}</th>
                  <th>{t('NLSEducationDocumentProgressChangeCol')}</th>
                  <th>{t('NLSEducationDocumentProgressTargetCol')}</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.progress.metrics.map((m) => {
                  const firstStr = m.first ? `${m.first.value} ${m.unit} (${m.first.date})` : '—'
                  const latestStr = m.latest ? `${m.latest.value} ${m.unit} (${m.latest.date})` : '—'
                  const changeStr = m.change ? `${m.change.diff > 0 ? '+' : ''}${m.change.diff} ${m.unit} (${m.change.trend})` : '—'
                  const targetStr = m.target != null ? `${m.target} ${m.unit}` : '—'
                  return (
                    <tr key={m.metricId}>
                      <td><strong>{m.name}</strong></td>
                      <td>{firstStr}</td>
                      <td>{latestStr}</td>
                      <td>{changeStr}</td>
                      <td>{targetStr}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          {snapshot.progress.level && (
            <div className="reportCardDocument__summaryBox">
              <strong>{t('NLSEducationDocumentProgressLevelHeader')}: </strong>
              {snapshot.progress.level.current
                ? (snapshot.progress.level.current.since
                    ? t('NLSEducationDocumentProgressCurrentLevelSince', {
                        level: snapshot.progress.level.current.name,
                        since: snapshot.progress.level.current.since,
                      })
                    : t('NLSEducationDocumentProgressCurrentLevel', {
                        level: snapshot.progress.level.current.name,
                      }))
                : '—'}
              {snapshot.progress.level.promotions && snapshot.progress.level.promotions.length > 0 && (
                <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                  {snapshot.progress.level.promotions.map((p, idx) => (
                    <li key={idx}>
                      {p.date}: {p.fromLevelName || '—'} &rarr; {p.toLevelName}
                      {p.note ? ` (${p.note})` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {snapshot.progress.practice && (
            <div className="reportCardDocument__summaryBox">
              {t('NLSEducationDocumentProgressPracticeSummary', {
                minutes: snapshot.progress.practice.totalMinutes,
                count: snapshot.progress.practice.logCount,
              })}
            </div>
          )}
        </div>
      )}

      {/* Attendance */}
      {snapshot?.attendance && (
        <div className="reportCardDocument__section">
          <span className="reportCardDocument__sectionTitle">
            {t('NLSEducationDocumentAttendanceHeader')}
          </span>
          <div className="reportCardDocument__summaryBox">
            {t('NLSEducationDocumentAttendanceSummary', {
              present: snapshot.attendance.present,
              late: snapshot.attendance.late,
              absent: snapshot.attendance.absent,
              onLeave: snapshot.attendance.onLeave,
              excused: snapshot.attendance.excused,
              percent: `${snapshot.attendance.percent}%`,
            })}
          </div>
        </div>
      )}

      {/* Rank (only when rank is present) */}
      {snapshot?.rank != null && snapshot.rank.rank != null && (
        <div className="reportCardDocument__section">
          <span className="reportCardDocument__sectionTitle">
            {t('NLSEducationDocumentRankHeader')}
          </span>
          <div className="reportCardDocument__summaryBox">
            {t('NLSEducationDocumentRankSummary', {
              rank: snapshot.rank.rank,
              classSize: snapshot.rank.classSize,
              overallPercent: `${snapshot.rank.overallPercent}%`,
            })}
          </div>
        </div>
      )}

      {/* Co-Scholastic (only when areas are present) */}
      {snapshot?.coScholastic && snapshot.coScholastic.length > 0 && (
        <div className="reportCardDocument__section">
          <span className="reportCardDocument__sectionTitle">
            {t('NLSEducationDocumentCoScholasticHeader')}
          </span>
          <table className="reportCardDocument__table">
            <thead>
              <tr>
                <th>{t('NLSEducationGradingSchemeAreaName')}</th>
                <th>{t('NLSEducationGradingSchemeScaleLabel')}</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.coScholastic.map((cs, idx) => (
                <tr key={cs.areaId || idx}>
                  <td><strong>{cs.name}</strong></td>
                  <td>{cs.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Overall Remark */}
      {snapshot?.overallRemark && (
        <div className="reportCardDocument__section">
          <span className="reportCardDocument__sectionTitle">
            {t('NLSEducationDocumentRemarksHeader')}
          </span>
          <div className="reportCardDocument__summaryBox">
            <strong>{t('NLSEducationDocumentOverallRemark')}</strong> {snapshot.overallRemark}
          </div>
        </div>
      )}

      {/* Signer Footer */}
      {snapshot?.signer && (
        <div className="reportCardDocument__footer">
          <div className="reportCardDocument__signer">
            {(() => {
              const sigSrc = signatureUrl || snapshot.signer.signatureUrl || undefined
              if (!sigSrc) return null
              return (
                <img
                  src={sigSrc}
                  alt={t('NLSEducationDocumentSignatureAlt')}
                  className="reportCardDocument__signature"
                />
              )
            })()}
            <span className="reportCardDocument__signerName">{snapshot.signer.name}</span>
            {snapshot.signer.title && (
              <span className="reportCardDocument__signerTitle">{snapshot.signer.title}</span>
            )}
          </div>
          {snapshot.signer.publishDate && (
            <div className="reportCardDocument__date">
              <span>{t('NLSEducationDocumentDateLabel')} </span>
              <strong>{snapshot.signer.publishDate}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
